"use client";

import { useDeferredValue, useMemo, useRef, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Field,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  TABLE,
  TD,
  TEXTAREA_PROSE,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BalanceIssue,
  type BalanceOptions,
  checkBalance,
  DEFAULT_BALANCE_OPTIONS,
} from "@/lib/tool-kit/style-checkers/bracket-balance";

/** English strings. A translated content file overrides these through `ui`. */
export const PUNCTUATION_BALANCE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a draft, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Participants (n = 120 completed the survey.\n\nOne said it was “fine and another called it “useful”.\n\nDetails are in the appendix [Table 2) and the results were consistent.",
  optionsLabel: "What to check",
  perParagraph:
    "Check each paragraph on its own (untick for dialogue that runs on)",
  listMarkers: "Ignore list markers at the start of a line, such as 1) and a)",
  smileys: "Ignore smileys such as :) and :-(",
  singleQuotes: "Pair curly single quotes ‘ ’ (a ’ with no ‘ is an apostrophe)",
  enterPrompt:
    "Paste or type some text to find brackets and quotation marks that are never closed or never opened.",
  results: "Results",
  problems: "Problems",
  unclosed: "Never closed",
  unopened: "Never opened",
  lines: "Lines",
  readingNone:
    "Every bracket and quotation mark is paired. That covers ( ), [ ], { }, curly and guillemet quotes, and straight double quotes; straight apostrophes are not checked.",
  readingSome:
    "Each row below is a mark with no partner. Use Show to select it in the text box above.",
  balanceHeading: "Marks counted",
  colMarks: "Marks",
  colOpening: "Opening",
  colClosing: "Closing",
  colDifference: "Difference",
  straightQuoteLabel: '" " (straight)',
  issuesHeading: "Problems found",
  colProblem: "Problem",
  colLine: "Line",
  colColumn: "Column",
  colContext: "Context",
  show: "Show",
  unclosedMessage: "{mark} is never closed. Add {missing} where it should end.",
  unopenedMessage:
    "{mark} closes nothing. Add {missing} where it should start, or delete this mark.",
  straightNote:
    "Straight quotes look the same at both ends, so the checker judges each from the characters around it.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with unmatched marks highlighted",
  legendUnclosed: "Never closed",
  legendUnopened: "Never opened",
  limits:
    'The check pairs marks by their shape, not by meaning. An apostrophe, a list marker, a smiley or a measurement such as 5" can look like a bracket or quote, and a quotation that continues over several paragraphs needs the paragraph option switched off.',
} as const;

type Strings = typeof PUNCTUATION_BALANCE_CHECKER_STRINGS;

const TONES = { unclosed: "rose", unopened: "amber" } as const;
const MAX_ROWS = 100;

function message(issue: BalanceIssue, strings: Strings): string {
  const template =
    issue.kind === "unclosed"
      ? strings.unclosedMessage
      : strings.unopenedMessage;
  return t(template, { mark: issue.char, missing: issue.missing });
}

function Context({ issue }: { issue: BalanceIssue }) {
  const before = issue.context.slice(0, issue.contextOffset);
  const mark = issue.context.slice(
    issue.contextOffset,
    issue.contextOffset + issue.char.length
  );
  const after = issue.context.slice(issue.contextOffset + issue.char.length);
  return (
    <code className="whitespace-pre-wrap break-words text-xs">
      {before}
      <mark className={HIGHLIGHT_TONES.rose}>{mark}</mark>
      {after}
    </code>
  );
}

export default function PunctuationBalanceCheckerWidget() {
  const strings = useToolUi(PUNCTUATION_BALANCE_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [options, setOptions] = useState<BalanceOptions>(
    DEFAULT_BALANCE_OPTIONS
  );
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => checkBalance(deferred, options),
    [deferred, options]
  );
  const hasText = deferred.trim().length > 0;
  const unclosed = report.issues.filter((i) => i.kind === "unclosed").length;
  const unopened = report.issues.length - unclosed;

  function setOption(key: keyof BalanceOptions, value: boolean) {
    setOptions((current) => ({ ...current, [key]: value }));
  }

  function reveal(issue: BalanceIssue) {
    const area = areaRef.current;
    if (!area || text !== deferred) {
      return;
    }
    area.focus();
    area.setSelectionRange(issue.start, issue.end);
  }

  const highlights: TextMatch[] = report.issues.map((issue) => ({
    start: issue.start,
    end: issue.end,
    category: issue.kind,
    title: message(issue, strings),
  }));
  const rows = report.tallies.map((tally) =>
    tally.open === tally.close
      ? [strings.straightQuoteLabel, fmt(tally.opens), "—", "—"]
      : [
          `${tally.open} ${tally.close}`,
          fmt(tally.opens),
          fmt(tally.closes),
          fmt(tally.opens - tally.closes),
        ]
  );

  return (
    <div className="space-y-6">
      <Field hint={strings.privacy} id="pbc-text" label={strings.textLabel}>
        <textarea
          className={TEXTAREA_PROSE}
          id="pbc-text"
          onChange={(event) => setText(event.target.value)}
          placeholder={strings.placeholder}
          ref={areaRef}
          rows={10}
          value={text}
        />
      </Field>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <fieldset className="space-y-2">
        <legend className="font-medium text-foreground text-sm">
          {strings.optionsLabel}
        </legend>
        <Checkbox
          checked={options.perParagraph}
          label={strings.perParagraph}
          onChange={(value) => setOption("perParagraph", value)}
        />
        <Checkbox
          checked={options.ignoreListMarkers}
          label={strings.listMarkers}
          onChange={(value) => setOption("ignoreListMarkers", value)}
        />
        <Checkbox
          checked={options.ignoreSmileys}
          label={strings.smileys}
          onChange={(value) => setOption("ignoreSmileys", value)}
        />
        <Checkbox
          checked={options.singleQuotes}
          label={strings.singleQuotes}
          onChange={(value) => setOption("singleQuotes", value)}
        />
      </fieldset>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.problems}
                value={fmt(report.issues.length)}
              />
              <StatCard label={strings.unclosed} value={fmt(unclosed)} />
              <StatCard label={strings.unopened} value={fmt(unopened)} />
              <StatCard label={strings.lines} value={fmt(report.lines)} />
            </StatGrid>
            <Notice>
              {report.issues.length === 0
                ? strings.readingNone
                : strings.readingSome}
            </Notice>
          </div>

          {report.issues.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.issuesHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.colProblem}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colLine}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colContext}
                      </th>
                      <th className={TH} scope="col">
                        <span className="sr-only">{strings.show}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.issues.slice(0, MAX_ROWS).map((issue) => (
                      <tr key={issue.start}>
                        <td className={TD}>{message(issue, strings)}</td>
                        <td className={TD}>{fmt(issue.line)}</td>
                        <td className={TD}>{fmt(issue.column)}</td>
                        <td className={TD}>
                          <Context issue={issue} />
                        </td>
                        <td className={TD}>
                          <button
                            className={BUTTON_SMALL}
                            onClick={() => reveal(issue)}
                            type="button"
                          >
                            {strings.show}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {report.tallies.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.balanceHeading}</h3>
              <DataTable
                head={[
                  strings.colMarks,
                  strings.colOpening,
                  strings.colClosing,
                  strings.colDifference,
                ]}
                rows={rows}
              />
              {report.tallies.some((tally) => tally.open === tally.close) ? (
                <p className={HINT}>{strings.straightNote}</p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendUnclosed}
              </span>
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendUnopened}
              </span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
          </div>
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
